<template>
  <UIDrawer
    :is-open
    :z-index
    :title="drawerTitle"
    size="lg"
    flat
    :allow-tray
    @minimize="handleMinimize"
    @close="handleClose"
  >
    <template v-if="!isNewAsset" #title>
      <AssetTitle v-bind="titleProps" />
    </template>
    <template v-if="!isNewAsset" #actions>
      <UITrashCan @click="handleDelete" />
    </template>
    <AssetThumbnail :instance />
    <div class="asset-drawer">
      <template v-for="tab in tabsList" :key="tab.route">
        <div
          v-if="initedTabs[tab.route]"
          v-show="tab.route === activeTab"
          class="asset-drawer__content"
          :class="{
            'asset-drawer__content--active': tab.route === activeTab,
          }"
        >
          <component
            :is="getComponent(tab.route)"
            :ref="e => componentRefs[tab.route] = e"
            :instance
            :amount
            :path
            :is-leaf
            :currency="isCurrency"
            @close="handleClose"
            @refresh:analytics="handleRefreshAnalytics"
          />
        </div>
      </template>
      <div class="asset-drawer__panel">
        <UIButtonIcon
          v-for="tab in tabsList"
          :key="tab.route"
          :data-refid="tab.dataRefId"
          :label="tab.label"
          :trailing="tab.icon"
          :variant="isActive(tab.route) ? 'secondary' : 'gray'"
          :fill="isActive(tab.route) ? undefined : 'outlined'"
          :disabled="tab.disabled"
          size="sm"
          @click="handleClickTab(tab.route)"
        />
      </div>
    </div>
  </UIDrawer>
</template>

<script setup lang="ts">
import { computed, markRaw, onBeforeMount, provide, ref, watchEffect } from 'vue'
import { RouteRecordNameGeneric, useRoute, useRouter } from 'vue-router'
import { orderBy } from 'lodash'

import { AssetClass } from '..'
import { ModalClass } from '@types'
import { ModalFactoryInstance } from '@/components/App/ModalFactory/utils/types'

import { ASSET_FIELD, ROUTE_NAME } from '@/const'

import { useModalsStore } from '@/store/modals'
import { useAssetsDeletedStore } from '@/store/assets/deleted'

import { UITrashCan } from '@ui/core'
import { UIButtonIcon } from '@ui/buttons'
import { UIDrawer, UIDialogRemove } from '@ui/modals'
import AssetForm from './AssetDrawerForm.vue'
import AssetContacts from './AssetDrawerContacts.vue'
import AssetDocuments from './AssetDrawerDocuments.vue'
import AssetPrices from './AssetDrawerPrices.vue'
import AssetPositions from './AssetDrawerPositions.vue'
import AssetSecurityMaster from './AssetDrawerSecurityMaster.vue'
import AssetTags from './AssetDrawerTags.vue'
import AssetTitle from './AssetDrawerTitle.vue'
import AssetThumbnail from './AssetThumbnail.vue'
import AssetTransactions from './AssetDrawerTransactions.vue'
import AssetValues from './AssetDrawerValues.vue'

import LineChartIcon from '@/assets/images/icons/line-chart.svg'
import {
  ArchiveBoxXMarkIcon,
  ArrowsRightLeftIcon,
  ChartBarIcon,
  DocumentTextIcon,
  InformationCircleIcon,
  TableCellsIcon,
  TagIcon,
  UserGroupIcon,
} from '@heroicons/vue/24/outline'

type Props = {
  modal: ModalClass<AssetClass>
  instance?: AssetClass
  restrictions?: string[]
  amount?: number
  path?: string[]
  isLeaf?: boolean
}

const props = defineProps<Props>()

const modalsStore = useModalsStore()
const assetsDeletedStore = useAssetsDeletedStore()

const route = useRoute()
const router = useRouter()

const currentTab = defineModel<string | undefined>('tab')

const componentRefs = ref<Record<string, any>>({})

const initedTabs = ref<Record<string, boolean>>({})

provide('restrictions', props.restrictions)

const activeTab = computed(
  () =>
    currentTab.value ||
    (isCurrency.value ? ROUTE_NAME.CURRENCIES_ITEM : ROUTE_NAME.ASSETS_ITEM),
)

const instance = computed(() => props.instance || props.modal.entity)
const isCurrency = computed(() => instance.value.isCurrency)

const parentRoute = computed(() =>
  isCurrency.value ? ROUTE_NAME.CURRENCIES : ROUTE_NAME.ASSETS,
)

const itemRoute = computed(() =>
  isCurrency.value ? ROUTE_NAME.CURRENCIES_ITEM : ROUTE_NAME.ASSETS_ITEM,
)

const securityMasterRoute = computed(() =>
  isCurrency.value
    ? ROUTE_NAME.CURRENCIES_ITEM_SECURITY_MASTER
    : ROUTE_NAME.ASSETS_ITEM_SECURITY_MASTER,
)

const tagsRoute = computed(() =>
  isCurrency.value
    ? ROUTE_NAME.CURRENCIES_ITEM_TAGS
    : ROUTE_NAME.ASSETS_ITEM_TAGS,
)

const pricesRoute = computed(() =>
  isCurrency.value
    ? ROUTE_NAME.CURRENCIES_ITEM_PRICES
    : ROUTE_NAME.ASSETS_ITEM_PRICES,
)

const isItAssetsRoute = computed(() => {
  return route.name?.toString().startsWith(parentRoute.value)
})

const isNewAsset = computed(() => instance.value.isNew)

const drawerTitle = computed(() => {
  return isNewAsset.value ? 'New Asset' : assetName.value
})

const isItRoutedAsset = computed(() => {
  return (
    route.matched.some(item => item.name === itemRoute.value) &&
    route.params?.id === instance.value.id
  )
})

const assetType = computed(
  () => instance.value.field<string>(ASSET_FIELD.TYPE).value,
)

const assetTicker = computed(
  () => instance.value.field<string>(ASSET_FIELD.TICKER).value,
)

const assetName = computed(
  () => instance.value.field<string>(ASSET_FIELD.NAME).value,
)

const tabsList = computed(() => {
  return [
    {
      label: 'Basic info',
      route: itemRoute.value,
      icon: InformationCircleIcon,
    },
    {
      label: 'Tags',
      route: tagsRoute.value,
      icon: TagIcon,
      disabled: isNewAsset.value,
      dataRefId: 'assetDrawerTags',
    },
    {
      label: 'Prices',
      route: pricesRoute.value,
      icon: LineChartIcon,
      disabled: isNewAsset.value,
      dataRefId: 'assetDrawerPrices',
    },
    ...(isCurrency.value
      ? []
      : [
        {
          label: 'Values',
          route: ROUTE_NAME.ASSETS_ITEM_VALUES,
          icon: ChartBarIcon,
          disabled: isNewAsset.value,
        },
      ]),
    ...(isCurrency.value
      ? []
      : [
        {
          label: 'Positions',
          route: ROUTE_NAME.ASSETS_ITEM_POSITIONS,
          icon: TableCellsIcon,
          disabled: isNewAsset.value,
        },
        {
          label: 'Transactions',
          route: ROUTE_NAME.ASSETS_ITEM_TRANSACTIONS,
          icon: ArrowsRightLeftIcon,
          disabled: isNewAsset.value,
        },
      ]),
    {
      label: 'Sec master',
      route: securityMasterRoute.value,
      icon: ArchiveBoxXMarkIcon,
      disabled: isNewAsset.value,
    },
    ...(isCurrency.value
      ? []
      : [
        {
          label: 'Contacts',
          route: ROUTE_NAME.ASSETS_ITEM_CONTACTS,
          icon: UserGroupIcon,
          disabled: isNewAsset.value,
          dataRefId: 'assetDrawerContacts',
        },
        {
          label: 'Documents',
          route: ROUTE_NAME.ASSETS_ITEM_DOCUMENTS,
          icon: DocumentTextIcon,
          disabled: isNewAsset.value,
          dataRefId: 'assetDrawerDocuments',
        },
      ]),
  ]
})

const isOpen = computed(() => props.modal.isOpened)
const zIndex = computed(() => props.modal.zIndex)
const allowTray = computed(() => props.modal.allowTray)

const showDate = computed(
  () =>
    isItRoutedAsset.value &&
    activeTab.value === ROUTE_NAME.ASSETS_ITEM_TRANSACTIONS,
)

const titleProps = computed(() => ({
  name: assetName.value,
  ticker: assetTicker.value,
  type: assetType.value,
  showDate: showDate.value,
}))

const getComponent = (routeName: string) => {
  switch (routeName) {
    case ROUTE_NAME.ASSETS_ITEM_SECURITY_MASTER:
    case ROUTE_NAME.CURRENCIES_ITEM_SECURITY_MASTER:
      return AssetSecurityMaster
    case ROUTE_NAME.ASSETS_ITEM_PRICES:
    case ROUTE_NAME.ASSETS_ITEM_PRICES_TABLE:
    case ROUTE_NAME.CURRENCIES_ITEM_PRICES:
    case ROUTE_NAME.CURRENCIES_ITEM_PRICES_TABLE:
      return AssetPrices
    case ROUTE_NAME.ASSETS_ITEM_POSITIONS:
      return AssetPositions
    case ROUTE_NAME.ASSETS_ITEM_TRANSACTIONS:
      return AssetTransactions
    case ROUTE_NAME.ASSETS_ITEM_VALUES:
      return AssetValues
    case ROUTE_NAME.ASSETS_ITEM_TAGS:
    case ROUTE_NAME.CURRENCIES_ITEM_TAGS:
      return AssetTags
    case ROUTE_NAME.ASSETS_ITEM_CONTACTS:
      return AssetContacts
    case ROUTE_NAME.ASSETS_ITEM_DOCUMENTS:
      return AssetDocuments
    default:
      return AssetForm
  }
}

const isActive = (name: string) => activeTab.value === name

const handleClickTab = (name: string) => {
  currentTab.value = name
  if (!isItRoutedAsset.value) return
  router.push({
    name,
    params: { id: instance.value.id },
  })
}

const handleMinimize = () => {
  props.modal.hide()
}

const handleClose = () => {
  props.modal.conditionalClose()
  const nextModal = orderBy<ModalFactoryInstance>(Object.values(modalsStore.getOpened), 'zIndex', 'desc').find(
    item => item.entity instanceof AssetClass,
  )
  if (!nextModal) return
  router.push({
    name: (nextModal.entity as AssetClass).isCurrency
      ? ROUTE_NAME.CURRENCIES_ITEM
      : ROUTE_NAME.ASSETS_ITEM,
    // @ts-expect-error - nextModal.entity is an AssetClass
    params: { id: nextModal.entity.id },
  })
}

const getRouteName = (routeName: RouteRecordNameGeneric) => {
  switch (routeName) {
    case ROUTE_NAME.ASSETS_ITEM_PRICES_TABLE:
      return ROUTE_NAME.ASSETS_ITEM_PRICES
    case ROUTE_NAME.CURRENCIES_ITEM_PRICES_TABLE:
      return ROUTE_NAME.CURRENCIES_ITEM_PRICES
    case ROUTE_NAME.ASSETS:
    case ROUTE_NAME.CURRENCIES:
      return
    default:
      return routeName?.toString()
  }
}

const handleRefreshAnalytics = () => {
  if (!initedTabs.value[ROUTE_NAME.ASSETS_ITEM_VALUES]) return
  componentRefs.value[ROUTE_NAME.ASSETS_ITEM_VALUES]?.refresh?.()
}

const handleDelete = () => {
  const assetName = instance.value.field<string>(ASSET_FIELD.NAME).value
  const modalInstance = modalsStore.init('asset-delete-confirm', markRaw(UIDialogRemove))

  const entityTitle = isCurrency.value ? 'currency' : 'asset'

  modalInstance?.open(modalsStore.getZIndex(), {
    title: `Delete ${entityTitle} '${assetName}'`,
    message: `Are you sure you want to delete this ${entityTitle}?`,
    label: 'Delete',
    onRemove: async () => {
      await instance.value.destroy()
      handleClose()
      await assetsDeletedStore.fetch()
    }
  })
}

watchEffect(() => {
  activeTab.value && (initedTabs.value[activeTab.value] = true)
})

onBeforeMount(() => {
  if (activeTab.value?.startsWith(ROUTE_NAME.ASSETS_ITEM) && isCurrency.value) {
    currentTab.value = currentTab.value?.replace(
      ROUTE_NAME.ASSETS_ITEM,
      ROUTE_NAME.CURRENCIES_ITEM,
    )
  }
  if (currentTab.value || !isItAssetsRoute.value) return
  currentTab.value = getRouteName(route.name)
})
</script>

<style>
.asset-drawer {
  @apply flex flex-auto flex-row-reverse;
  @apply overflow-hidden;

  &__content {
    @apply flex flex-col flex-auto;
    @apply overflow-hidden;
  }

  &__panel {
    @apply flex flex-col shrink-0;
    @apply relative;
    @apply gap-4 py-2 px-1;
    @apply shrink-0;
    @apply bg-body-gray;
    @apply border-r border-light-border;
    @apply overflow-y-auto;

    .ui-button-icon__label {
      @apply max-w-none;
    }
  }

  &__form {
    @apply flex;
    @apply gap-2 py-4 px-4 sm:px-default;

    .ui-panel {
      @apply flex-auto;
      @apply m-0;
    }

    &--second {
      @apply pt-0;
    }
  }

  &__form-input {
    @apply flex-auto;
  }

  &__pagination {
    @apply px-4 sm:px-default;
  }
}
</style>
